"use client"

import { FormEvent, useEffect, useRef, useState } from "react"
import { useParams, useRouter } from "next/navigation"
import { MagnifyingGlassIcon } from "@modules/common/icons"

import { searchClient, SEARCH_INDEX_NAME } from "@lib/search-client"
import LocalizedClientLink from "@modules/common/components/localized-client-link"

type SearchHit = {
  id: string
  title: string
  handle: string
  thumbnail: string | null
}

const MAX_RESULTS = 6
const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 200

export default function HeaderSearch() {
  const [value, setValue] = useState("")
  const [hits, setHits] = useState<SearchHit[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  const router = useRouter()
  const { countryCode } = useParams<{ countryCode?: string }>()
  const containerRef = useRef<HTMLDivElement>(null)
  const mobileInputRef = useRef<HTMLInputElement>(null)

  const trimmed = value.trim()
  const prefix = countryCode ? `/${countryCode}` : ""

  // Live search against Meilisearch, debounced as the user types.
  useEffect(() => {
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setHits([])
      setLoading(false)
      return
    }

    setLoading(true)
    let cancelled = false

    const timer = setTimeout(async () => {
      try {
        const { results } = (await searchClient.search([
          { indexName: SEARCH_INDEX_NAME, params: { query: trimmed } },
        ])) as { results: { hits: SearchHit[] }[] }

        if (!cancelled) {
          setHits((results?.[0]?.hits ?? []).slice(0, MAX_RESULTS))
        }
      } catch {
        if (!cancelled) setHits([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, DEBOUNCE_MS)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [trimmed])

  // Close the dropdowns when clicking outside the search box.
  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
        setMobileOpen(false)
      }
    }
    document.addEventListener("mousedown", onPointerDown)
    return () => document.removeEventListener("mousedown", onPointerDown)
  }, [])

  // Focus the input as soon as the mobile panel pops down.
  useEffect(() => {
    if (mobileOpen) {
      mobileInputRef.current?.focus()
    }
  }, [mobileOpen])

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!trimmed) return
    setOpen(false)
    setMobileOpen(false)
    router.push(`${prefix}/results/${encodeURIComponent(trimmed)}`)
  }

  const closeAndClear = () => {
    setOpen(false)
    setMobileOpen(false)
    setValue("")
  }

  const showResults = trimmed.length >= MIN_QUERY_LENGTH

  const resultsContent = showResults ? (
    hits.length > 0 ? (
      <ul className="max-h-[70vh] overflow-y-auto py-1">
        {hits.map((hit) => (
          <li key={hit.id}>
            <LocalizedClientLink
              href={`/products/${hit.handle}`}
              onClick={closeAndClear}
              className="flex items-center gap-3 px-3 py-2 hover:bg-aov-surface transition-colors"
            >
              {hit.thumbnail ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={hit.thumbnail}
                  alt=""
                  className="h-10 w-10 shrink-0 rounded object-contain bg-aov-light-grey"
                />
              ) : (
                <div className="h-10 w-10 shrink-0 rounded bg-aov-light-grey" />
              )}
              <span className="line-clamp-2 text-[13px] font-medium text-aov-navy">
                {hit.title}
              </span>
            </LocalizedClientLink>
          </li>
        ))}
        <li className="border-t border-aov-border">
          <LocalizedClientLink
            href={`/results/${encodeURIComponent(trimmed)}`}
            onClick={() => {
              setOpen(false)
              setMobileOpen(false)
            }}
            className="block px-3 py-2.5 text-[12px] font-semibold text-aov-orange hover:bg-aov-surface transition-colors"
          >
            View all results for &ldquo;{trimmed}&rdquo;
          </LocalizedClientLink>
        </li>
      </ul>
    ) : (
      <div className="px-3 py-4 text-[13px] text-aov-navy/60">
        {loading ? "Searching…" : `No products match “${trimmed}”.`}
      </div>
    )
  ) : null

  return (
    <div ref={containerRef} className="md:relative flex items-center">
      {/* Desktop: inline search box */}
      <form
        onSubmit={onSubmit}
        role="search"
        className="hidden md:flex items-center gap-2 rounded-[10px] bg-white pl-3 pr-1 py-1 ring-1 ring-aov-border focus-within:ring-aov-navy/30 transition-colors"
      >
        <MagnifyingGlassIcon className="h-4 w-4 text-aov-text-muted" />
        <input
          type="search"
          value={value}
          onChange={(e) => {
            setValue(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setOpen(false)
          }}
          placeholder="Search products"
          aria-label="Search products"
          className="w-44 lg:w-56 bg-transparent text-[13px] text-aov-navy placeholder:text-aov-text-muted focus:outline-none py-1"
        />
        <button
          type="submit"
          disabled={!trimmed}
          aria-label="Submit search"
          className="ml-1 inline-flex items-center justify-center rounded-[8px] bg-aov-orange px-3 py-1.5 text-[12px] font-semibold text-white transition-colors duration-200 hover:bg-aov-orange-hover disabled:bg-aov-border disabled:text-aov-text-light disabled:cursor-not-allowed"
        >
          Search
        </button>
      </form>

      {/* Mobile: icon trigger, panel pops down below the nav row */}
      <button
        type="button"
        onClick={() => setMobileOpen((o) => !o)}
        aria-label="Search products"
        aria-expanded={mobileOpen}
        className="md:hidden inline-flex items-center justify-center p-2 text-aov-navy transition-colors hover:text-aov-orange"
      >
        <MagnifyingGlassIcon className="h-[22px] w-[22px]" />
      </button>

      {/* Anchored to the sticky header wrapper, not the icon, so it can
          span the viewport width on small screens. */}
      {mobileOpen && (
        <div className="md:hidden absolute inset-x-3 top-full mt-2 z-50 overflow-hidden rounded-[10px] border border-aov-border bg-white shadow-xl">
          <form
            onSubmit={onSubmit}
            role="search"
            className={`flex items-center gap-2 p-2 ${
              showResults ? "border-b border-aov-border" : ""
            }`}
          >
            <MagnifyingGlassIcon className="ml-1 h-4 w-4 shrink-0 text-aov-text-muted" />
            <input
              ref={mobileInputRef}
              type="search"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setMobileOpen(false)
              }}
              placeholder="Search products"
              aria-label="Search products"
              className="min-w-0 flex-1 bg-transparent text-[13px] text-aov-navy placeholder:text-aov-text-muted focus:outline-none py-1.5"
            />
            <button
              type="submit"
              disabled={!trimmed}
              aria-label="Submit search"
              className="inline-flex shrink-0 items-center justify-center rounded-[8px] bg-aov-orange px-3 py-1.5 text-[12px] font-semibold text-white transition-colors duration-200 hover:bg-aov-orange-hover disabled:bg-aov-border disabled:text-aov-text-light disabled:cursor-not-allowed"
            >
              Search
            </button>
          </form>
          {resultsContent}
        </div>
      )}

      {/* Desktop live results dropdown */}
      {open && showResults && (
        <div className="hidden md:block absolute right-0 top-full mt-2 z-50 w-[360px] overflow-hidden rounded-[10px] border border-aov-border bg-white shadow-xl">
          {resultsContent}
        </div>
      )}
    </div>
  )
}
